import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { AlertTriangle, X } from 'lucide-react'
import { IGNORED_FILTERS_EVENT, type IgnoredFiltersDetail } from '@/api/client'

/**
 * "This list does not support filter X" — shown when the API reports
 * `meta.filters.unrecognized`, so a filter that could not be applied is never
 * invisible (defect D-002, _specs/40_E2E_TEST_STRATEGY.md §3.2).
 *
 * LAYOUT CONTRACT: this is an INLINE banner in normal document flow. It is
 * rendered at the top of <main>, it pushes the page content down, and it never
 * paints over a table, a chart or a KPI tile. It was briefly implemented as a
 * fixed-position toast, which the headless layout scan caught overlapping the
 * /attendance table header, chart labels and a KPI value at 390–1440px.
 * Do not give this element `position: fixed` or `absolute`.
 *
 * It wraps rather than spills at narrow widths: the row stacks below `sm`, and
 * the message uses `break-words` so a long filter-key list cannot overflow.
 */
export function FilterNotice() {
  const location = useLocation()
  const [keys, setKeys] = useState<string[]>([])

  // A notice belongs to the page that provoked it.
  useEffect(() => setKeys([]), [location.pathname, location.search])

  useEffect(() => {
    const onIgnored = (e: Event) => {
      const detail = (e as CustomEvent<IgnoredFiltersDetail>).detail
      setKeys((cur) => Array.from(new Set([...cur, ...detail.unrecognized])))
    }
    window.addEventListener(IGNORED_FILTERS_EVENT, onIgnored)
    return () => window.removeEventListener(IGNORED_FILTERS_EVENT, onIgnored)
  }, [])

  if (keys.length === 0) return null

  const plural = keys.length > 1

  return (
    <div
      role="status"
      className="mb-4 flex flex-col gap-2 rounded-lg border border-warning/30 bg-warning-subtle p-3 text-warning-content sm:flex-row sm:items-start sm:gap-3"
    >
      <AlertTriangle aria-hidden="true" className="h-4 w-4 shrink-0 sm:mt-0.5" />
      <div className="min-w-0 flex-1 text-xs leading-relaxed break-words">
        <span className="font-semibold">Filter not applied. </span>
        This list does not support the filter{plural ? 's' : ''}{' '}
        <span className="font-mono font-semibold break-all">{keys.join(', ')}</span>
        {' — '}the results below are <strong>not</strong> filtered by {plural ? 'them' : 'it'}.
      </div>
      <button
        type="button"
        onClick={() => setKeys([])}
        aria-label="Dismiss filter notice"
        className="shrink-0 self-end rounded p-1 text-warning-content hover:bg-warning-subtle sm:self-start"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}
